import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowUp, ArrowDown, Eye, EyeOff, Save, GripVertical } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const SECTION_LABELS = {
  slides: { label: "Slides (banner topo)", icon: "🎞️", desc: "Carrossel grande no topo da home." },
  segments: { label: "Descubra pelo desejo (segmentos)", icon: "✨", desc: "Cards dos segmentos cadastrados pelo admin." },
  cities: { label: "Escolha sua cidade", icon: "📍", desc: "Grid de cidades atendidas." },
  shop: { label: "Shop em destaque", icon: "🛍️", desc: "6 produtos do Shop como teaser na home." },
  how_it_works: { label: "Como funciona (Encontre / Compre / Use)", icon: "💡", desc: "3 passos curtos." },
  guides: { label: "Guias das cidades", icon: "📖", desc: "Cards estilo livro com PDF." },
  ctas: { label: "Atalhos (Empresas / Clientes)", icon: "🚀", desc: "Dois cards de chamada para ação." },
  highlights: { label: "Destaques selecionados", icon: "⭐", desc: "Empresas em destaque (admin marca)." },
};
const ALL_KEYS = ["slides", "segments", "cities", "shop", "how_it_works", "guides", "ctas", "highlights"];

export default function AdminHomeSections() {
  const [order, setOrder] = useState(ALL_KEYS);
  const [hidden, setHidden] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const cfg = r.data?.home_sections_order;
      const arr = Array.isArray(cfg) && cfg.length ? cfg : ALL_KEYS;
      // hidden = all - present
      const present = new Set(arr);
      const _hidden = new Set(ALL_KEYS.filter((k) => !present.has(k)));
      setOrder([...arr, ...ALL_KEYS.filter((k) => !present.has(k))]);
      setHidden(_hidden);
    }).finally(() => setLoading(false));
  }, []);

  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= order.length) return;
    const arr = [...order];
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setOrder(arr);
  };

  const toggleHide = (key) => {
    const s = new Set(hidden);
    s.has(key) ? s.delete(key) : s.add(key);
    setHidden(s);
  };

  const save = async () => {
    setBusy(true);
    try {
      // Visible sections in the chosen order
      const visible = order.filter((k) => !hidden.has(k));
      // Also fetch current settings to preserve other fields (PATCH replaces whole doc per backend)
      const cur = await api.get("/admin/settings").then((r) => r.data);
      const payload = { ...cur };
      delete payload.id;
      payload.home_sections_order = visible;
      await api.patch("/admin/settings", payload);
      toast.success("Ordem da home salva");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao salvar"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-stone-400">Carregando...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"><ArrowLeft size={14}/> Voltar</Link>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-1">Ordem das seções da home</h1>
          <p className="text-stone-500 text-sm mt-1">Arraste pra cima/baixo para reordenar. Use o olho para ocultar.</p>
        </div>
        <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="hs-save">
          <Save size={14} className="mr-1.5"/> {busy ? "Salvando..." : "Salvar"}
        </Button>
      </div>

      <Card className="p-4 rounded-2xl border-stone-200">
        <ol className="space-y-2">
          {order.map((key, idx) => {
            const meta = SECTION_LABELS[key] || { label: key, icon: "•" };
            const isHidden = hidden.has(key);
            return (
              <li key={key} className={`flex items-center gap-3 p-3 rounded-xl border ${isHidden ? "border-stone-200 bg-stone-50/50 opacity-60" : "border-stone-200 bg-white"}`} data-testid={`hs-row-${key}`}>
                <GripVertical size={16} className="text-stone-300"/>
                <span className="text-2xl">{meta.icon}</span>
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">{idx + 1}. {meta.label}</p>
                  {meta.desc && <p className="text-xs text-stone-500">{meta.desc}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded-lg" data-testid={`hs-up-${key}`}><ArrowUp size={14}/></Button>
                  <Button variant="ghost" size="sm" onClick={() => move(idx, 1)} disabled={idx === order.length - 1} className="rounded-lg" data-testid={`hs-down-${key}`}><ArrowDown size={14}/></Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleHide(key)} className={`rounded-lg ${isHidden ? "text-red-500" : "text-emerald-700"}`} data-testid={`hs-toggle-${key}`}>
                    {isHidden ? <EyeOff size={14}/> : <Eye size={14}/>}
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      </Card>

      <p className="text-xs text-stone-500">A nova ordem é refletida na home pública assim que você salvar.</p>
    </div>
  );
}
